import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Clock, BookOpen, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { formatDate } from '../../utils/helpers';

const subjectPill = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700', 'bg-teal-100 text-teal-700',
  'bg-yellow-100 text-yellow-800', 'bg-red-100 text-red-700',
];

const FILTERS = ['Class Wise', 'Student Wise', 'Monthly', 'Weekly'];

function getWeekRange(offset = 0) {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = d => d.toISOString().split('T')[0];
  return { start: fmt(monday), end: fmt(sunday), label: `${fmt(monday)} – ${fmt(sunday)}` };
}

function getMonthRange(year, month) {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const last = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { start, end };
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function HomeworkReports() {
  const { homework, classes, subjects, students, hwCompletions } = useApp();
  const today = new Date().toISOString().split('T')[0];

  const [filter, setFilter] = useState('Class Wise');
  const now = new Date();

  // Class wise
  const [selectedClass, setSelectedClass] = useState(classes[0]?.id || '');

  // Student wise
  const [stuClass, setStuClass] = useState(classes[0]?.id || '');
  const stuClassStudents = students.filter(s => s.classId === stuClass && s.status === 'active').sort((a, b) => Number(a.rollNumber) - Number(b.rollNumber));
  const [selectedStudent, setSelectedStudent] = useState(stuClassStudents[0]?.id || '');
  const [stuMonthYear, setStuMonthYear] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const prevStuMonth = () => setStuMonthYear(p => p.m === 1 ? { y: p.y - 1, m: 12 } : { y: p.y, m: p.m - 1 });
  const nextStuMonth = () => setStuMonthYear(p => p.m === 12 ? { y: p.y + 1, m: 1 } : { y: p.y, m: p.m + 1 });

  // Monthly
  const [monthYear, setMonthYear] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const prevMonth = () => setMonthYear(p => p.m === 1 ? { y: p.y - 1, m: 12 } : { y: p.y, m: p.m - 1 });
  const nextMonth = () => setMonthYear(p => p.m === 12 ? { y: p.y + 1, m: 1 } : { y: p.y, m: p.m + 1 });

  // Weekly
  const [weekOffset, setWeekOffset] = useState(0);
  const week = getWeekRange(weekOffset);

  // --- helpers ---
  const getSubject = id => subjects.find(s => s.id === id);
  const getClass   = id => classes.find(c => c.id === id);
  const getStudent = id => students.find(s => s.id === id);
  const completionCount = hwId => new Set((hwCompletions || []).filter(c => c.homeworkId === hwId).map(c => c.studentId)).size;
  const classStudentCount = cid => students.filter(s => s.classId === cid && s.status === 'active').length;

  // --- filtered data ---
  const filteredHw = useMemo(() => {
    if (filter === 'Class Wise')
      return homework.filter(h => h.classId === selectedClass);
    if (filter === 'Student Wise') {
      const stu = getStudent(selectedStudent);
      return stu ? homework.filter(h => h.classId === stu.classId) : [];
    }
    if (filter === 'Monthly') {
      const { start, end } = getMonthRange(monthYear.y, monthYear.m);
      return homework.filter(h => h.dueDate >= start && h.dueDate <= end);
    }
    if (filter === 'Weekly')
      return homework.filter(h => h.dueDate >= week.start && h.dueDate <= week.end);
    return homework;
  }, [filter, selectedClass, selectedStudent, monthYear, week.start, week.end, homework]);

  // Group by subject for class/student views; by date for monthly/weekly
  const grouped = useMemo(() => {
    if (filter === 'Class Wise' || filter === 'Student Wise') {
      return filteredHw.reduce((acc, hw) => {
        const key = hw.subjectId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(hw);
        return acc;
      }, {});
    }
    // date-grouped
    return filteredHw.reduce((acc, hw) => {
      const key = hw.dueDate || 'Unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(hw);
      return acc;
    }, {});
  }, [filteredHw, filter]);

  const sortedKeys = useMemo(() => {
    const keys = Object.keys(grouped);
    if (filter === 'Monthly' || filter === 'Weekly') return keys.sort();
    return keys;
  }, [grouped, filter]);

  const completedByStudent = hwId => {
    const stu = getStudent(selectedStudent);
    return (hwCompletions || []).some(c => c.homeworkId === hwId && c.studentId === stu?.id);
  };

  // Summary stats
  const total     = filteredHw.length;
  const duePast   = filteredHw.filter(h => h.dueDate < today).length;
  const dueToday  = filteredHw.filter(h => h.dueDate === today).length;
  const upcoming  = filteredHw.filter(h => h.dueDate > today).length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Homework Reports</h1>
        <p className="text-gray-500 text-sm">{total} assignment{total !== 1 ? 's' : ''} in current view</p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap mb-5">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-1 rounded-lg text-sm font-medium border transition-colors ${filter === f ? 'text-white border-blue-900' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
            style={filter === f ? { backgroundColor: '#1e3a8a' } : {}}>
            {f}
          </button>
        ))}
      </div>

      {/* Filter controls */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 mb-5 flex flex-wrap items-center gap-4">
        {filter === 'Class Wise' && (
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Class</label>
            <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
            </select>
            <span className="">{classStudentCount(selectedClass)} students</span>
          </div>
        )}
        {filter === 'Student Wise' && (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Class</label>
              <select value={stuClass} onChange={e => { setStuClass(e.target.value); setSelectedStudent(students.filter(s => s.classId === e.target.value && s.status === 'active')[0]?.id || ''); }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {classes.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Student</label>
              <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {stuClassStudents.length === 0
                  ? <option value="">No students</option>
                  : stuClassStudents.map(s => (
                    <option key={s.id} value={s.id}>{s.rollNumber}. {s.firstName} {s.lastName}</option>
                  ))}
              </select>
            </div>
            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
              <label className="text-sm font-medium">Month</label>
              <button onClick={prevStuMonth} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronLeft size={16} /></button>
              <span className="font-semibold min-w-[110px] text-center text-sm">
                {MONTH_NAMES[stuMonthYear.m - 1]} {stuMonthYear.y}
              </span>
              <button onClick={nextStuMonth} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
        {filter === 'Monthly' && (
          <div className="flex items-center gap-3">
            <button onClick={prevMonth} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronLeft size={16} /></button>
            <span className="font-semibold min-w-[120px] text-center">
              {MONTH_NAMES[monthYear.m - 1]} {monthYear.y}
            </span>
            <button onClick={nextMonth} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronRight size={16} /></button>
          </div>
        )}
        {filter === 'Weekly' && (
          <div className="flex items-center gap-3">
            <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronLeft size={16} /></button>
            <span className="font-semibold min-w-[200px] text-center text-sm">
              {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : weekOffset === 1 ? 'Next Week' : week.label}
              <span className="block font-normal">{week.label}</span>
            </span>
            <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronRight size={16} /></button>
          </div>
        )}

        {/* Quick stats */}
        <div className="ml-auto flex gap-3">
          <span className="bg-gray-100 px-2.5 py-1 rounded-lg font-medium">Total: {total}</span>
          <span className="bg-red-50 text-red-600 px-2.5 py-1 rounded-lg font-medium">Past due: {duePast}</span>
          <span className="bg-orange-50 text-orange-600 px-2.5 py-1 rounded-lg font-medium">Today: {dueToday}</span>
          <span className="bg-green-50 text-green-600 px-2.5 py-1 rounded-lg font-medium">Upcoming: {upcoming}</span>
        </div>
      </div>

      {/* Results */}
      {filter === 'Class Wise' ? (
        /* ── Matrix: Students (rows) × Homework (cols) ── */
        (() => {
          const classStudents = students
            .filter(s => s.classId === selectedClass && s.status === 'active')
            .sort((a, b) => Number(a.rollNumber) - Number(b.rollNumber));
          const hwList = homework
            .filter(h => h.classId === selectedClass)
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

          // Group hw columns by date for merged header
          const dateGroups = hwList.reduce((acc, hw) => {
            const d = hw.dueDate || 'Unknown';
            if (!acc[d]) acc[d] = [];
            acc[d].push(hw);
            return acc;
          }, {});
          const sortedDates = Object.keys(dateGroups).sort();

          // isConfirmed: check if a student's parent has confirmed
          const studentDone = (hwId, studentId) =>
            (hwCompletions || []).some(c => c.homeworkId === hwId && c.studentId === studentId);

          if (hwList.length === 0)
            return <div className="bg-white rounded-xl p-12 text-center">No homework assigned for this class</div>;
          if (classStudents.length === 0)
            return <div className="bg-white rounded-xl p-12 text-center">No active students in this class</div>;

          return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Summary bar */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <BookOpen size={15} className="text-gray-500" />
                  <span className="text-sm font-semibold">{classStudents.length} Students</span>
                  <span className="">·</span>
                  <span className="text-sm font-semibold">{hwList.length} Homework items</span>
                </div>
                <div className="flex gap-2">
                  <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium"><CheckCircle size={10}/> Done</span>
                  <span className="flex items-center gap-1 bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium"><X size={10}/> Not Done</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="text-sm border-collapse" style={{ minWidth: `${180 + hwList.length * 90}px` }}>
                  <thead>
                    {/* Row 1: Date headers (merged) */}
                    <tr className="bg-gray-100 border-b border-gray-200">
                      <th className="sticky left-0 bg-gray-100 px-4 py-1.5 text-left font-semibold border-r border-gray-200 min-w-[180px]">Student</th>
                      {sortedDates.map(date => {
                        const cols = dateGroups[date];
                        const isToday = date === today;
                        const isPast  = date < today;
                        return (
                          <th key={date} colSpan={cols.length}
                            className={`px-2 py-2.5 text-center font-bold border-r border-gray-200 ${isToday ? 'text-orange-600 bg-orange-50' : isPast ? 'text-gray-500' : 'text-green-700 bg-green-50'}`}>
                            {formatDate(date)}{isToday ? ' ·Today' : ''}
                          </th>
                        );
                      })}
                      <th className="px-3 py-1 text-center font-semibold border-l border-gray-200 min-w-[80px]">Score</th>
                    </tr>
                    {/* Row 2: Subject / title headers */}
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="sticky left-0 bg-gray-50 px-4 py-1 text-left font-semibold border-r border-gray-200">Roll · Name</th>
                      {hwList.map((hw, idx) => {
                        const sub = getSubject(hw.subjectId);
                        return (
                          <th key={hw.id} className="px-2 py-2 text-center border-r border-gray-200 min-w-[80px]">
                            <span className={`block text-[10px] font-bold px-1.5 py-0.5 rounded-full ${subjectPill[idx % subjectPill.length]}`}>{sub?.name || '—'}</span>
                            <span className="block text-[10px] mt-0.5 truncate max-w-[80px]" title={hw.title}>{hw.title}</span>
                          </th>
                        );
                      })}
                      <th className="px-3 py-1 text-center font-semibold border-l border-gray-200">Done/Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {classStudents.map((stu, sIdx) => {
                      const doneCount = hwList.filter(hw => studentDone(hw.id, stu.id)).length;
                      const pct = hwList.length > 0 ? Math.round((doneCount / hwList.length) * 100) : 0;
                      return (
                        <tr key={stu.id} className={sIdx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100'}>
                          <td className={`sticky left-0 px-4 py-1.5 border-r border-gray-200 ${sIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-2">
                              <span className="w-6 text-right flex-shrink-0">{stu.rollNumber}</span>
                              <div>
                                <p className="font-medium leading-tight">{stu.firstName} {stu.lastName}</p>
                              </div>
                            </div>
                          </td>
                          {hwList.map(hw => {
                            const done = studentDone(hw.id, stu.id);
                            return (
                              <td key={hw.id} className="px-2 py-2.5 text-center border-r border-gray-100">
                                {done
                                  ? <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100"><CheckCircle size={15} className="text-green-600" /></span>
                                  : <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-50"><X size={15} className="text-red-500" /></span>
                                }
                              </td>
                            );
                          })}
                          <td className="px-3 py-1 text-center border-l border-gray-200">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`font-bold ${pct === 100 ? 'text-green-600' : pct > 50 ? 'text-blue-600' : 'text-red-500'}`}>
                                {doneCount}/{hwList.length}
                              </span>
                              <div className="w-12 bg-gray-200 rounded-full h-1.5">
                                <div className="h-1.5 rounded-full bg-green-500" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Footer: per-hw completion count */}
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td className="sticky left-0 bg-gray-50 px-4 py-1 font-bold border-r border-gray-200">Completion</td>
                      {hwList.map(hw => {
                        const c = completionCount(hw.id);
                        const t = classStudents.length;
                        return (
                          <td key={hw.id} className="px-2 py-2 text-center border-r border-gray-100">
                            <span className={`font-bold ${c === t ? 'text-green-600' : c > 0 ? 'text-gray-600' : 'text-red-500'}`}>{c}/{t}</span>
                          </td>
                        );
                      })}
                      <td className="px-3 py-1 text-center border-l border-gray-200">
                        <span className="font-bold">
                          {Math.round(hwList.reduce((s, hw) => s + completionCount(hw.id), 0) / Math.max(hwList.length, 1))}/{classStudents.length}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()
      ) : filteredHw.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">No homework found for this selection</div>
      ) : filter === 'Student Wise' ? (
        /* Student wise: Date × Subject matrix */
        (() => {
          const stu = getStudent(selectedStudent);
          const cls = getClass(stu?.classId);
          const { start: stuStart, end: stuEnd } = getMonthRange(stuMonthYear.y, stuMonthYear.m);
          const hwList = homework.filter(h => h.classId === stu?.classId && h.dueDate >= stuStart && h.dueDate <= stuEnd);

          // Unique sorted due dates
          const dates = [...new Set(hwList.map(h => h.dueDate || 'Unknown'))].sort();

          // Unique subjects that appear in this class's homework
          const subjectIds = [...new Set(hwList.map(h => h.subjectId))];
          const subjectList = subjectIds.map(id => getSubject(id)).filter(Boolean);

          // Lookup: { [date]: { [subjectId]: hw } }
          const lookup = {};
          hwList.forEach(hw => {
            const d = hw.dueDate || 'Unknown';
            if (!lookup[d]) lookup[d] = {};
            lookup[d][hw.subjectId] = hw;
          });

          const doneCount = hwList.filter(h => completedByStudent(h.id)).length;
          const pct = hwList.length > 0 ? Math.round((doneCount / hwList.length) * 100) : 0;

          if (hwList.length === 0)
            return <div className="bg-white rounded-xl p-12 text-center">No homework found for {MONTH_NAMES[stuMonthYear.m - 1]} {stuMonthYear.y}</div>;

          return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Student summary bar */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#1e3a8a' }}>
                    {stu?.firstName?.[0]}{stu?.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{stu?.firstName} {stu?.lastName}</p>
                    <p className="">Class {cls?.name} – Section {cls?.section} · Roll {stu?.rollNumber}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium"><CheckCircle size={11}/> {doneCount} Done</span>
                    <span className="flex items-center gap-1 bg-red-100 text-red-600 px-2.5 py-1 rounded-full font-medium"><X size={11}/> {hwList.length - doneCount} Pending</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div className="h-2 rounded-full bg-green-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-bold">{pct}%</span>
                  </div>
                </div>
              </div>

              {/* Matrix: rows = subjects, columns = dates */}
              <div className="overflow-x-auto">
                <table className="text-sm border-collapse" style={{ minWidth: `${160 + dates.length * 100}px` }}>
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-200">
                      <th className="sticky left-0 bg-gray-100 px-5 py-2.5 text-left font-semibold border-r border-gray-200 min-w-[140px]">Subject</th>
                      {dates.map(date => {
                        const isToday = date === today;
                        const isPast  = date < today;
                        return (
                          <th key={date} className={`px-3 py-1 text-center border-r border-gray-200 min-w-[100px] ${isToday ? 'bg-orange-50' : isPast ? 'bg-gray-100' : 'bg-green-50'}`}>
                            <span className={`block text-[11px] font-bold ${isToday ? 'text-orange-600' : isPast ? 'text-gray-500' : 'text-green-700'}`}>
                              {isToday ? 'Today' : formatDate(date)}
                            </span>
                          </th>
                        );
                      })}
                      <th className="px-3 py-1 text-center font-semibold bg-gray-100 min-w-[70px]">Done</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {subjectList.map((sub, sIdx) => {
                      const subDone  = dates.filter(d => lookup[d]?.[sub.id] && completedByStudent(lookup[d][sub.id].id)).length;
                      const subTotal = dates.filter(d => lookup[d]?.[sub.id]).length;
                      return (
                        <tr key={sub.id} className={sIdx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/40 hover:bg-gray-100'}>
                          <td className={`sticky left-0 px-5 py-3 border-r border-gray-200 ${sIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <span className={`inline-block font-bold px-2.5 py-1 rounded-full ${subjectPill[sIdx % subjectPill.length]}`}>
                              {sub.name}
                            </span>
                          </td>
                          {dates.map(date => {
                            const hw = lookup[date]?.[sub.id];
                            if (!hw) {
                              return (
                                <td key={date} className="px-3 py-3 text-center border-r border-gray-100">
                                  <span className="text-gray-300 text-sm">—</span>
                                </td>
                              );
                            }
                            const done = completedByStudent(hw.id);
                            return (
                              <td key={date} className={`px-3 py-3 text-center border-r border-gray-100 ${done ? 'bg-green-50/50' : ''}`}>
                                <div className="flex flex-col items-center gap-0.5">
                                  {done
                                    ? <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100"><CheckCircle size={15} className="text-green-600" /></span>
                                    : <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-50"><X size={15} className="text-red-500" /></span>
                                  }
                                  <span className="text-[10px] truncate max-w-[80px]" title={hw.title}>{hw.title}</span>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-3 py-3 text-center">
                            <span className={`font-bold ${subDone === subTotal && subTotal > 0 ? 'text-green-600' : subDone > 0 ? 'text-blue-600' : 'text-red-500'}`}>
                              {subDone}/{subTotal}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()
      ) : (
        /* Monthly/Weekly: Subject × Date matrix, one table per class */
        (() => {
          // Build class → date → subject → hw lookup
          const classIds = [...new Set(filteredHw.map(h => h.classId))];
          const allDates  = [...new Set(filteredHw.map(h => h.dueDate || 'Unknown'))].sort();

          if (classIds.length === 0)
            return <div className="bg-white rounded-xl p-12 text-center">No homework found for this period</div>;

          return (
            <div className="space-y-5">
              {classIds.map(cid => {
                const cls = getClass(cid);
                const clsHw = filteredHw.filter(h => h.classId === cid);
                const tot = classStudentCount(cid);

                // Dates that have at least one hw for this class
                const clsDates = allDates.filter(d => clsHw.some(h => (h.dueDate || 'Unknown') === d));

                // Unique subjects for this class
                const subIds = [...new Set(clsHw.map(h => h.subjectId))];
                const subList = subIds.map(id => getSubject(id)).filter(Boolean);

                // Lookup: date → subjectId → hw
                const lk = {};
                clsHw.forEach(hw => {
                  const d = hw.dueDate || 'Unknown';
                  if (!lk[d]) lk[d] = {};
                  lk[d][hw.subjectId] = hw;
                });

                return (
                  <div key={cid} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    {/* Class header */}
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
                      <span className="text-sm font-bold">{cls ? `${cls.name} – Section ${cls.section}` : '—'}</span>
                      <span className="">{clsDates.length} date{clsDates.length !== 1 ? 's' : ''} · {subList.length} subject{subList.length !== 1 ? 's' : ''} · {tot} students</span>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="text-sm border-collapse" style={{ minWidth: `${160 + clsDates.length * 110}px` }}>
                        <thead>
                          <tr className="bg-gray-100 border-b border-gray-200">
                            <th className="sticky left-0 bg-gray-100 px-5 py-2.5 text-left font-semibold border-r border-gray-200 min-w-[140px]">Subject</th>
                            {clsDates.map(date => {
                              const isToday = date === today;
                              const isPast  = date < today;
                              return (
                                <th key={date} className={`px-3 py-1 text-center border-r border-gray-200 min-w-[110px] ${isToday ? 'bg-orange-50' : isPast ? 'bg-gray-100' : 'bg-green-50'}`}>
                                  <span className={`block text-[11px] font-bold ${isToday ? 'text-orange-600' : isPast ? 'text-gray-500' : 'text-green-700'}`}>
                                    {isToday ? 'Today' : formatDate(date)}
                                  </span>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {subList.map((sub, sIdx) => (
                            <tr key={sub.id} className={sIdx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/40 hover:bg-gray-100'}>
                              <td className={`sticky left-0 px-5 py-3 border-r border-gray-200 ${sIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <span className={`inline-block font-bold px-2.5 py-1 rounded-full ${subjectPill[sIdx % subjectPill.length]}`}>
                                  {sub.name}
                                </span>
                              </td>
                              {clsDates.map(date => {
                                const hw = lk[date]?.[sub.id];
                                if (!hw) return (
                                  <td key={date} className="px-3 py-3 text-center border-r border-gray-100">
                                    <span className="text-gray-300">—</span>
                                  </td>
                                );
                                const done    = completionCount(hw.id);
                                const notDone = tot - done;
                                const pct     = tot > 0 ? Math.round((done / tot) * 100) : 0;
                                return (
                                  <td key={date} className={`px-2 py-2 text-center border-r border-gray-100 ${pct === 100 ? 'bg-green-50/40' : ''}`}>
                                    <div className="flex flex-col items-center gap-1">
                                      <div className="flex items-center gap-1.5">
                                        <span className="inline-flex items-center gap-0.5 text-[11px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                          <CheckCircle size={9}/>{done}
                                        </span>
                                        <span className="inline-flex items-center gap-0.5 text-[11px] font-bold bg-red-50 text-red-500 px-1.5 py-0.5 rounded">
                                          <X size={9}/>{notDone}
                                        </span>
                                      </div>
                                      <div className="w-14 bg-gray-200 rounded-full h-1">
                                        <div className="h-1 rounded-full bg-green-500" style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      )}
    </div>
  );
}
